import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Snackbar,
  useTheme,
  alpha
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ColorLens as ColorLensIcon,
  Image as ImageIcon,
  Palette as PaletteIcon,
  FontDownload as FontIcon,
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const AssetCard = ({ asset, onDelete, onDownload }) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);

  const getAssetIcon = (type) => {
    switch (type) {
      case 'logo':
      case 'icon':
      case 'image':
        return <ImageIcon />;
      case 'color':
        return <PaletteIcon />;
      case 'font':
        return <FontIcon />;
      default:
        return <ImageIcon />;
    }
  };

  const isImage = asset.mimeType?.startsWith('image/');

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isImage && !imageError ? (
        <CardMedia
          component="img"
          height="140"
          image={asset.url}
          alt={asset.name}
          sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
          onError={() => setImageError(true)}
        />
      ) : (
        <Box
          sx={{
            height: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1)
          }}
        >
          {getAssetIcon(asset.type)}
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="subtitle1" component="div" noWrap>
          {asset.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={asset.type} size="small" color="primary" variant="outlined" />
          <Chip
            label={`${(asset.size / 1024).toFixed(1)} KB`}
            size="small"
            variant="outlined"
          />
        </Box>
        {asset.metadata?.dimensions && (
          <Typography variant="caption" color="text.secondary">
            {asset.metadata.dimensions.width} × {asset.metadata.dimensions.height}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        <Tooltip title="Download">
          <IconButton size="small" onClick={() => onDownload(asset)}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(asset)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const ColorSchemeEditor = ({ colorScheme, onChange }) => {
  const [activeColor, setActiveColor] = useState('primary');
  const [showPicker, setShowPicker] = useState(false);

  const colorFields = [
    { key: 'primary', label: 'Primary Color' },
    { key: 'secondary', label: 'Secondary Color' },
    { key: 'accent', label: 'Accent Color' },
    { key: 'text.primary', label: 'Primary Text' },
    { key: 'text.secondary', label: 'Secondary Text' },
    { key: 'background.default', label: 'Background' },
    { key: 'background.paper', label: 'Paper Background' }
  ];

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((acc, key) => {
      if (!acc[key]) acc[key] = {};
      return acc[key];
    }, obj);
    target[lastKey] = value;
    return { ...obj };
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Color Scheme
      </Typography>
      <Grid container spacing={2}>
        {colorFields.map((field) => {
          const value = getNestedValue(colorScheme, field.key) || '#000000';
          return (
            <Grid
              key={field.key}
              size={{
                xs: 12,
                sm: 6
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: value,
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setActiveColor(field.key);
                    setShowPicker(true);
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{field.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {value}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <Dialog open={showPicker} onClose={() => setShowPicker(false)}>
        <DialogTitle>
          Choose Color - {colorFields.find(f => f.key === activeColor)?.label}
        </DialogTitle>
        <DialogContent>
          <HexColorPicker
            color={getNestedValue(colorScheme, activeColor) || '#000000'}
            onChange={(hex) => {
              const newScheme = setNestedValue(colorScheme, activeColor, hex);
              onChange(newScheme);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPicker(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// eslint-disable-next-line max-lines-per-function
function BrandAssetManager() {
  useAuth();
  const [assets, setAssets] = useState([]);
  const [colorScheme, setColorScheme] = useState({
    primary: '#1976d2',
    secondary: '#dc004e',
    accent: '#9c27b0',
    text: {
      primary: '#000000',
      secondary: '#666666'
    },
    background: {
      default: '#ffffff',
      paper: '#f5f5f5'
    }
  });
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadAssets();
    loadColorScheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const response = await api.listBrandAssets('global');
      setAssets(response.assets || []);
    } catch (error) {
      console.error('Failed to load assets:', error);
      showSnackbar('Failed to load assets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadColorScheme = async () => {
    try {
      const response = await api.getColorScheme('global');
      if (response.colorScheme) {
        setColorScheme(response.colorScheme);
      }
    } catch (error) {
      console.error('Failed to load color scheme:', error);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', 'global');

    setUploading(true);
    try {
      showSnackbar('Asset uploaded successfully', 'success');
      setUploadDialogOpen(false);
      loadAssets();
    } catch (error) {
      console.error('Failed to upload asset:', error);
      showSnackbar('Failed to upload asset', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete ${asset.name}?`)) return;

    try {
      await api.deleteBrandAsset('global', asset.id);
      showSnackbar('Asset deleted successfully', 'success');
      loadAssets();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      showSnackbar('Failed to delete asset', 'error');
    }
  };

  const handleDownload = (asset) => {
    if (asset.url) {
      window.open(asset.url, '_blank');
    }
  };

  const handleSaveColorScheme = async () => {
    try {
      await api.updateColorScheme('global', colorScheme);
      showSnackbar('Color scheme saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save color scheme:', error);
      showSnackbar('Failed to save color scheme', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Global Brand Assets
        </Typography>
      </Box>
      {/* Color Scheme Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <ColorSchemeEditor
          colorScheme={colorScheme}
          onChange={setColorScheme}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<ColorLensIcon />}
            onClick={handleSaveColorScheme}
          >
            Save Color Scheme
          </Button>
        </Box>
      </Paper>
      {/* Assets Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Brand Assets
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Asset
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : assets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No assets uploaded yet
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {assets.map((asset) => (
              <Grid
                key={asset.id}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                  lg: 3
                }}>
                <AssetCard
                  asset={asset}
                  onDelete={handleDelete}
                  onDownload={handleDownload}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Brand Asset</DialogTitle>
        <DialogContent>
          <input
            accept="image/*,.json,.ttf,.otf"
            style={{ display: 'none' }}
            id="asset-upload"
            type="file"
            onChange={handleUpload}
            disabled={uploading}
          />
          <label htmlFor="asset-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              fullWidth
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Choose File'}
            </Button>
          </label>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Supported: Images (PNG, JPG, SVG), Fonts (TTF, OTF), Colors (JSON)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BrandAssetManager;